import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Save, CreditCard, Building2, ExternalLink,
  CheckCircle, AlertCircle, Loader2, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';

// Payment method icons/logos as simple components
const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="#003087">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
  </svg>
);

const StripeIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="#635BFF">
    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
  </svg>
);

const KlarnaIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="#FFB3C7">
    <path d="M4.592 2v20H0V2h4.592zm10.456 0a8.04 8.04 0 0 1-3.166 6.396L8.938 11.39l5.128 6.748v3.862h-4.9l-4.86-6.393.002-.002a12.042 12.042 0 0 0 4.63-9.605H4.593V2h10.455zM24 18.857a3.143 3.143 0 1 1-6.286 0 3.143 3.143 0 0 1 6.286 0z"/>
  </svg>
);

const VendorPaymentSettings = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isHydrated, forceHydrate } = useAuthStore();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState({
    // PayPal
    paypal_enabled: false,
    paypal_me_link: '',
    paypal_email: '',
    // Stripe
    stripe_enabled: false,
    stripe_payment_link: '',
    // Klarna
    klarna_enabled: false,
    klarna_link: '',
    // Bank Transfer
    bank_transfer_enabled: false,
    bank_account_holder: '',
    bank_iban: '',
    bank_bic: '',
    bank_name: '',
    // Custom payment instructions
    custom_payment_instructions: ''
  });

  useEffect(() => {
    forceHydrate();
  }, [forceHydrate]);

  useEffect(() => {
    if (!isHydrated) return;
    
    if (!isAuthenticated) {
      navigate('/login?redirect=/vendor-dashboard/payment-settings');
      return;
    }
    
    if (user?.role !== 'vendor') {
      toast.error('Nur für Verkäufer zugänglich');
      navigate('/');
      return;
    }
    
    loadPaymentSettings();
  }, [isAuthenticated, user, navigate, isHydrated]);

  const loadPaymentSettings = async () => {
    try {
      const response = await axiosInstance.get('/vendors/me');
      const details = response.data?.vendor_profile?.payment_details || {};
      
      setPaymentSettings({
        paypal_enabled: !!details.paypal_me_link || !!details.paypal_email,
        paypal_me_link: details.paypal_me_link || '',
        paypal_email: details.paypal_email || '',
        stripe_enabled: !!details.stripe_payment_link,
        stripe_payment_link: details.stripe_payment_link || '',
        klarna_enabled: !!details.klarna_link,
        klarna_link: details.klarna_link || '',
        bank_transfer_enabled: !!details.bank_iban,
        bank_account_holder: details.bank_account_holder || '',
        bank_iban: details.bank_iban || '',
        bank_bic: details.bank_bic || '',
        bank_name: details.bank_name || '',
        custom_payment_instructions: details.custom_payment_instructions || ''
      });
    } catch (error) {
      console.error('Error loading payment settings:', error);
      toast.error('Fehler beim Laden der Zahlungseinstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build the payment details object
      const paymentDetails = {};
      
      if (paymentSettings.paypal_enabled) {
        if (paymentSettings.paypal_me_link) {
          paymentDetails.paypal_me_link = paymentSettings.paypal_me_link;
        }
        if (paymentSettings.paypal_email) {
          paymentDetails.paypal_email = paymentSettings.paypal_email;
        }
      }
      
      if (paymentSettings.stripe_enabled && paymentSettings.stripe_payment_link) {
        paymentDetails.stripe_payment_link = paymentSettings.stripe_payment_link;
      }
      
      if (paymentSettings.klarna_enabled && paymentSettings.klarna_link) {
        paymentDetails.klarna_link = paymentSettings.klarna_link;
      }
      
      if (paymentSettings.bank_transfer_enabled && paymentSettings.bank_iban) {
        paymentDetails.bank_account_holder = paymentSettings.bank_account_holder;
        paymentDetails.bank_iban = paymentSettings.bank_iban;
        paymentDetails.bank_bic = paymentSettings.bank_bic;
        paymentDetails.bank_name = paymentSettings.bank_name;
      }
      
      if (paymentSettings.custom_payment_instructions) {
        paymentDetails.custom_payment_instructions = paymentSettings.custom_payment_instructions;
      }

      await axiosInstance.put('/vendors/me/payment-details', paymentDetails);
      toast.success('Zahlungseinstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const hasAnyPaymentMethod = () => {
    return (
      (paymentSettings.paypal_enabled && (paymentSettings.paypal_me_link || paymentSettings.paypal_email)) ||
      (paymentSettings.stripe_enabled && paymentSettings.stripe_payment_link) ||
      (paymentSettings.klarna_enabled && paymentSettings.klarna_link) ||
      (paymentSettings.bank_transfer_enabled && paymentSettings.bank_iban)
    );
  };

  if (!isHydrated || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link to="/vendor-dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
            </Link>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-gray-900">Zahlungseinstellungen</h1>
              <p className="text-xs text-gray-500">Ihre Zahlungsmethoden für Kunden</p>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Speichern
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Banner */}
        {!hasAnyPaymentMethod() && (
          <Card className="mb-6 bg-yellow-50 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Keine Zahlungsmethode aktiv</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Bitte aktivieren Sie mindestens eine Zahlungsmethode, damit Kunden Sie bezahlen können.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {hasAnyPaymentMethod() && (
          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <p className="text-green-800">
                  Ihre Zahlungsmethoden sind eingerichtet. Kunden können Sie direkt bezahlen.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Box */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">So funktioniert es:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Sie geben Ihre Zahlungslinks ein (PayPal.me, Stripe, etc.)</li>
                  <li>Kunden sehen diese Links beim Checkout</li>
                  <li>Kunden klicken auf den Link und bezahlen direkt an Sie</li>
                  <li>Sie erhalten das Geld sofort auf Ihr Konto</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {/* PayPal */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <PayPalIcon />
                  </div>
                  <div>
                    <CardTitle className="text-lg">PayPal</CardTitle>
                    <CardDescription>Zahlung per PayPal.me Link oder E-Mail</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={paymentSettings.paypal_enabled}
                  onCheckedChange={(checked) => setPaymentSettings({...paymentSettings, paypal_enabled: checked})}
                />
              </div>
            </CardHeader>
            {paymentSettings.paypal_enabled && (
              <CardContent className="space-y-4 pt-0">
                <div>
                  <Label htmlFor="paypal_me_link">PayPal.me Link (empfohlen)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="paypal_me_link"
                      placeholder="https://paypal.me/IhrName"
                      value={paymentSettings.paypal_me_link}
                      onChange={(e) => setPaymentSettings({...paymentSettings, paypal_me_link: e.target.value})}
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => window.open('https://www.paypal.com/paypalme/', '_blank')}
                      title="PayPal.me erstellen"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Noch keinen PayPal.me Link? <a href="https://www.paypal.com/paypalme/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Hier erstellen</a>
                  </p>
                </div>
                <div>
                  <Label htmlFor="paypal_email">Oder: PayPal E-Mail</Label>
                  <Input
                    id="paypal_email"
                    type="email"
                    placeholder="ihre@email.de"
                    value={paymentSettings.paypal_email}
                    onChange={(e) => setPaymentSettings({...paymentSettings, paypal_email: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            )}
          </Card>

          {/* Stripe */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <StripeIcon />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Stripe</CardTitle>
                    <CardDescription>Zahlung per Stripe Payment Link</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={paymentSettings.stripe_enabled}
                  onCheckedChange={(checked) => setPaymentSettings({...paymentSettings, stripe_enabled: checked})}
                />
              </div>
            </CardHeader>
            {paymentSettings.stripe_enabled && (
              <CardContent className="space-y-4 pt-0">
                <div>
                  <Label htmlFor="stripe_payment_link">Stripe Payment Link</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="stripe_payment_link"
                      placeholder="https://buy.stripe.com/..."
                      value={paymentSettings.stripe_payment_link}
                      onChange={(e) => setPaymentSettings({...paymentSettings, stripe_payment_link: e.target.value})}
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => window.open('https://dashboard.stripe.com/payment-links', '_blank')}
                      title="Stripe Payment Link erstellen"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    <a href="https://dashboard.stripe.com/payment-links" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe Payment Link erstellen</a> (Stripe-Konto erforderlich)
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Klarna */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <KlarnaIcon />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Klarna</CardTitle>
                    <CardDescription>Zahlung per Klarna (Rechnung, Ratenkauf)</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={paymentSettings.klarna_enabled}
                  onCheckedChange={(checked) => setPaymentSettings({...paymentSettings, klarna_enabled: checked})}
                />
              </div>
            </CardHeader>
            {paymentSettings.klarna_enabled && (
              <CardContent className="space-y-4 pt-0">
                <div>
                  <Label htmlFor="klarna_link">Klarna Payment Link</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="klarna_link"
                      placeholder="https://app.klarna.com/..."
                      value={paymentSettings.klarna_link}
                      onChange={(e) => setPaymentSettings({...paymentSettings, klarna_link: e.target.value})}
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => window.open('https://www.klarna.com/de/verkaeufer/', '_blank')}
                      title="Klarna für Verkäufer"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    <a href="https://www.klarna.com/de/verkaeufer/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Klarna für Verkäufer</a>
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Bank Transfer */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Building2 className="h-6 w-6 text-green-700" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Banküberweisung</CardTitle>
                    <CardDescription>Klassische Überweisung (Vorkasse)</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={paymentSettings.bank_transfer_enabled}
                  onCheckedChange={(checked) => setPaymentSettings({...paymentSettings, bank_transfer_enabled: checked})}
                />
              </div>
            </CardHeader>
            {paymentSettings.bank_transfer_enabled && (
              <CardContent className="space-y-4 pt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="bank_account_holder">Kontoinhaber *</Label>
                    <Input
                      id="bank_account_holder"
                      placeholder="Max Mustermann"
                      value={paymentSettings.bank_account_holder}
                      onChange={(e) => setPaymentSettings({...paymentSettings, bank_account_holder: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="bank_iban">IBAN *</Label>
                    <Input
                      id="bank_iban"
                      placeholder="DE89 3704 0044 0532 0130 00"
                      value={paymentSettings.bank_iban}
                      onChange={(e) => setPaymentSettings({...paymentSettings, bank_iban: e.target.value})}
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank_bic">BIC</Label>
                    <Input
                      id="bank_bic"
                      placeholder="COBADEFFXXX"
                      value={paymentSettings.bank_bic}
                      onChange={(e) => setPaymentSettings({...paymentSettings, bank_bic: e.target.value})}
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank_name">Bank</Label>
                    <Input
                      id="bank_name"
                      placeholder="Commerzbank"
                      value={paymentSettings.bank_name}
                      onChange={(e) => setPaymentSettings({...paymentSettings, bank_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Custom Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Zusätzliche Hinweise</CardTitle>
              <CardDescription>
                Optionale Nachricht an Ihre Kunden (z.B. Verwendungszweck, Lieferhinweise)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <textarea
                className="w-full min-h-[100px] p-3 border rounded-lg text-sm resize-y"
                placeholder="z.B. Bitte geben Sie Ihre Bestellnummer als Verwendungszweck an..."
                value={paymentSettings.custom_payment_instructions}
                onChange={(e) => setPaymentSettings({...paymentSettings, custom_payment_instructions: e.target.value})}
              />
            </CardContent>
          </Card>
        </div>

        {/* Save Button (Bottom) */}
        <div className="mt-8 flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Einstellungen speichern
          </Button>
        </div>
      </main>
    </div>
  );
};

export default VendorPaymentSettings;
